import {
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type Key,
  type ReactNode,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import { classNames } from "./component-model";

export interface TableRegionProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "aria-label" | "tabIndex"> {
  readonly label: string;
}

export function TableRegion({
  label,
  className,
  children,
  ...props
}: TableRegionProps) {
  const regionRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const region = regionRef.current;
    if (!region) return;

    const updateScrollableState = () => {
      setIsScrollable(region.scrollWidth > region.clientWidth);
    };
    updateScrollableState();

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateScrollableState);
    observer?.observe(region);
    window.addEventListener("resize", updateScrollableState);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateScrollableState);
    };
  }, []);

  return (
    <div
      {...props}
      ref={regionRef}
      className={classNames("wh-table-region", className)}
      role="region"
      aria-label={label}
      tabIndex={isScrollable ? 0 : undefined}
    >
      {children}
    </div>
  );
}

export function Table({
  className,
  children,
  ...props
}: TableHTMLAttributes<HTMLTableElement>) {
  return (
    <table {...props} className={classNames("wh-table", className)}>
      {children}
    </table>
  );
}

export function TableCaption({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLTableCaptionElement>) {
  return (
    <caption {...props} className={classNames("wh-table__caption", className)}>
      {children}
    </caption>
  );
}

export function TableHead({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead {...props} className={classNames("wh-table__head", className)}>
      {children}
    </thead>
  );
}

export function TableBody({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody {...props} className={classNames("wh-table__body", className)}>
      {children}
    </tbody>
  );
}

export function TableRow({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr {...props} className={classNames("wh-table__row", className)}>
      {children}
    </tr>
  );
}

export interface TableHeaderCellProps
  extends Omit<ThHTMLAttributes<HTMLTableCellElement>, "align"> {
  readonly textAlign?: "start" | "center" | "end";
}

export function TableHeaderCell({
  scope = "col",
  textAlign = "start",
  className,
  children,
  ...props
}: TableHeaderCellProps) {
  return (
    <th
      {...props}
      scope={scope}
      className={classNames(
        "wh-table__header",
        `wh-table__cell--${textAlign}`,
        className,
      )}
    >
      {children}
    </th>
  );
}

export interface TableCellProps
  extends Omit<TdHTMLAttributes<HTMLTableCellElement>, "align"> {
  readonly textAlign?: "start" | "center" | "end";
}

export function TableCell({
  textAlign = "start",
  className,
  children,
  ...props
}: TableCellProps) {
  return (
    <td
      {...props}
      className={classNames(
        "wh-table__cell",
        `wh-table__cell--${textAlign}`,
        className,
      )}
    >
      {children}
    </td>
  );
}

export interface DataTableColumn<Row> {
  readonly id: string;
  readonly header: ReactNode;
  readonly render: (row: Row) => ReactNode;
  readonly align?: "start" | "center" | "end";
  readonly rowHeader?: boolean;
  readonly sortDirection?: "ascending" | "descending" | "none" | "other";
}

export interface DataTableProps<Row> {
  readonly regionLabel: string;
  readonly caption: ReactNode;
  readonly columns: readonly DataTableColumn<Row>[];
  readonly rows: readonly Row[];
  readonly getRowKey: (row: Row, index: number) => Key;
  readonly emptyMessage?: ReactNode;
  readonly className?: string;
}

export function DataTable<Row>({
  regionLabel,
  caption,
  columns,
  rows,
  getRowKey,
  emptyMessage = "No records are available.",
  className,
}: DataTableProps<Row>) {
  return (
    <TableRegion label={regionLabel}>
      <Table className={className}>
        <TableCaption>{caption}</TableCaption>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableHeaderCell
                key={column.id}
                textAlign={column.align ?? "start"}
                aria-sort={column.sortDirection}
              >
                {column.header}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                className="wh-table__empty"
                colSpan={Math.max(columns.length, 1)}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, rowIndex) => (
              <TableRow key={getRowKey(row, rowIndex)}>
                {columns.map((column) =>
                  column.rowHeader ? (
                    <TableHeaderCell
                      key={column.id}
                      scope="row"
                      textAlign={column.align ?? "start"}
                    >
                      {column.render(row)}
                    </TableHeaderCell>
                  ) : (
                    <TableCell
                      key={column.id}
                      textAlign={column.align ?? "start"}
                    >
                      {column.render(row)}
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableRegion>
  );
}
